import { Product } from '../types/product';
import { supabase } from '../lib/supabase';

// Convertir datos de Supabase a formato Product
const convertSupabaseProduct = (supabaseProduct: any): Product => ({
  id: supabaseProduct.id,
  name: supabaseProduct.name,
  category: supabaseProduct.category,
  price: supabaseProduct.price,
  description: supabaseProduct.description,
  image: supabaseProduct.image,
  rating: supabaseProduct.rating,
  reviews: supabaseProduct.reviews,
  inStock: supabaseProduct.in_stock,
  stock: supabaseProduct.stock || 0,
  featured: supabaseProduct.featured,
  specifications: supabaseProduct.specifications ? {
    brand: supabaseProduct.specifications.brand,
    model: supabaseProduct.specifications.model,
    material: supabaseProduct.specifications.material,
    dimensions: supabaseProduct.specifications.dimensions,
    weight: supabaseProduct.specifications.weight,
    warranty: supabaseProduct.specifications.warranty,
    origin: supabaseProduct.specifications.origin,
    technicalDetails: supabaseProduct.specifications.technical_details
  } : undefined,
  images: supabaseProduct.images,
  datasheet: supabaseProduct.datasheet
});

// Datos de ejemplo con especificaciones para pruebas
const getMockProductsWithSpecifications = (): Product[] => {
  return [
    {
      id: 1,
      name: "Rodamiento Industrial SKF 6305",
      category: "spares",
      price: 450.00,
      description: "Rodamiento de alta precisión para maquinaria pesada industrial. Diseñado para operar en condiciones extremas y garantizar máxima durabilidad.",
      image: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=500&h=400&fit=crop",
      rating: 4.8,
      reviews: 124,
      inStock: true,
      stock: 50,
      featured: true,
      specifications: {
        brand: "SKF",
        model: "6305-2RS1",
        material: "Acero de alto cromo",
        dimensions: "62mm x 17mm x 17mm",
        weight: "0.2 kg",
        warranty: "2 años",
        origin: "Suecia",
        technicalDetails: [
          "Velocidad máxima: 12,000 RPM",
          "Carga dinámica: 22.5 kN",
          "Carga estática: 11.5 kN",
          "Temperatura de operación: -30°C a 120°C",
          "Sellado: Doble sello de goma (2RS1)",
          "Precisión: P6 (ABEC 3)"
        ]
      },
      images: [
        "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=500&h=400&fit=crop",
        "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=500&h=400&fit=crop",
        "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=500&h=400&fit=crop"
      ],
      datasheet: "https://example.com/datasheets/skf-6305.pdf"
    },
    {
      id: 2,
      name: "Bomba Centrífuga Industrial",
      category: "machinery",
      price: 12500.00,
      description: "Bomba de alta eficiencia para procesos industriales. Ideal para transferencia de fluidos en plantas de procesamiento.",
      image: "https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?w=500&h=400&fit=crop",
      rating: 4.9,
      reviews: 89,
      inStock: true,
      stock: 15,
      featured: true,
      specifications: {
        brand: "Grundfos",
        model: "CRN-10",
        material: "Acero inoxidable 316L",
        dimensions: "800mm x 400mm x 600mm",
        weight: "125 kg",
        warranty: "3 años",
        origin: "Dinamarca",
        technicalDetails: [
          "Caudal máximo: 100 m³/h",
          "Altura manométrica: 80 m",
          "Potencia motor: 15 kW",
          "Velocidad: 2900 RPM",
          "Temperatura máxima: 120°C",
          "Presión máxima: 16 bar",
          "Conexiones: DN 50 (2\")"
        ]
      },
      images: [
        "https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?w=500&h=400&fit=crop",
        "https://images.unsplash.com/photo-1565034947150-d2390e7b9b8e?w=500&h=400&fit=crop"
      ],
      datasheet: "https://example.com/datasheets/grundfos-crn10.pdf"
    },
    {
      id: 3,
      name: "Kit de Herramientas de Precisión",
      category: "tools",
      price: 280.00,
      description: "Set completo de herramientas para mantenimiento industrial. Incluye todo lo necesario para trabajos de precisión.",
      image: "https://images.unsplash.com/photo-1532298227911-5a369e4c4bb5?w=500&h=400&fit=crop",
      rating: 4.7,
      reviews: 156,
      inStock: true,
      stock: 75,
      featured: false,
      specifications: {
        brand: "Stanley",
        model: "Pro-Kit 85",
        material: "Acero forjado y plástico reforzado",
        dimensions: "450mm x 300mm x 180mm",
        weight: "4.5 kg",
        warranty: "1 año",
        origin: "Estados Unidos",
        technicalDetails: [
          "85 piezas incluidas",
          "Maletín de aluminio resistente",
          "Herramientas con acabado cromado",
          "Mecanismo de trinquete de 72 dientes",
          "Adaptador de 1/4\" y 3/8\"",
          "Garantía de por vida contra defectos"
        ]
      },
      images: [
        "https://images.unsplash.com/photo-1532298227911-5a369e4c4bb5?w=500&h=400&fit=crop",
        "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=500&h=400&fit=crop"
      ]
    }
  ];
};

// Servicio de productos con Supabase
export const productService = {
  // Obtener todos los productos
  getProducts: async (): Promise<Product[]> => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error al obtener productos:', error);
        // Si hay error con Supabase, devolver datos de ejemplo
        return getMockProductsWithSpecifications();
      }

      // Si no hay productos en Supabase, devolver datos de ejemplo
      if (!data || data.length === 0) {
        return getMockProductsWithSpecifications();
      }

      return data.map(convertSupabaseProduct);
    } catch (error) {
      console.error('Error inesperado al obtener productos:', error);
      // En caso de error, devolver datos de ejemplo
      return getMockProductsWithSpecifications();
    }
  },

  // Obtener un producto por ID
  getProductById: async (id: number): Promise<Product | null> => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error al obtener producto por ID:', error);
        return null;
      }

      return convertSupabaseProduct(data);
    } catch (error) {
      console.error('Error inesperado al obtener producto por ID:', error);
      return null;
    }
  },

  // Agregar un nuevo producto
  addProduct: async (product: Omit<Product, 'id'>): Promise<Product | null> => {
    try {
      const { data, error } = await supabase
        .from('products')
        .insert([{
          name: product.name,
          category: product.category,
          price: product.price,
          description: product.description,
          image: product.image,
          rating: product.rating,
          reviews: product.reviews,
          in_stock: product.inStock,
          stock: product.stock || 0,
          featured: product.featured,
          specifications: product.specifications ? {
            brand: product.specifications.brand,
            model: product.specifications.model,
            material: product.specifications.material,
            dimensions: product.specifications.dimensions,
            weight: product.specifications.weight,
            warranty: product.specifications.warranty,
            origin: product.specifications.origin,
            technical_details: product.specifications.technicalDetails
          } : null,
          images: product.images,
          datasheet: product.datasheet
        }])
        .select()
        .single();

      if (error) {
        console.error('Error al agregar producto:', error);
        return null;
      }

      return convertSupabaseProduct(data);
    } catch (error) {
      console.error('Error inesperado al agregar producto:', error);
      return null;
    }
  },

  // Actualizar un producto existente
  updateProduct: async (id: number, updatedProduct: Partial<Product>): Promise<Product | null> => {
    try {
      const updateData: any = {};
      
      if (updatedProduct.name !== undefined) updateData.name = updatedProduct.name;
      if (updatedProduct.category !== undefined) updateData.category = updatedProduct.category;
      if (updatedProduct.price !== undefined) updateData.price = updatedProduct.price;
      if (updatedProduct.description !== undefined) updateData.description = updatedProduct.description;
      if (updatedProduct.image !== undefined) updateData.image = updatedProduct.image;
      if (updatedProduct.rating !== undefined) updateData.rating = updatedProduct.rating;
      if (updatedProduct.reviews !== undefined) updateData.reviews = updatedProduct.reviews;
      if (updatedProduct.inStock !== undefined) updateData.in_stock = updatedProduct.inStock;
      if (updatedProduct.stock !== undefined) updateData.stock = updatedProduct.stock;
      if (updatedProduct.featured !== undefined) updateData.featured = updatedProduct.featured;
      if (updatedProduct.specifications !== undefined) {
        updateData.specifications = updatedProduct.specifications ? {
          brand: updatedProduct.specifications.brand,
          model: updatedProduct.specifications.model,
          material: updatedProduct.specifications.material,
          dimensions: updatedProduct.specifications.dimensions,
          weight: updatedProduct.specifications.weight,
          warranty: updatedProduct.specifications.warranty,
          origin: updatedProduct.specifications.origin,
          technical_details: updatedProduct.specifications.technicalDetails
        } : null;
      }
      if (updatedProduct.images !== undefined) updateData.images = updatedProduct.images;
      if (updatedProduct.datasheet !== undefined) updateData.datasheet = updatedProduct.datasheet;

      const { data, error } = await supabase
        .from('products')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error al actualizar producto:', error);
        return null;
      }

      return convertSupabaseProduct(data);
    } catch (error) {
      console.error('Error inesperado al actualizar producto:', error);
      return null;
    }
  },

  // Eliminar un producto
  deleteProduct: async (id: number): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error al eliminar producto:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error inesperado al eliminar producto:', error);
      return false;
    }
  },

  // Obtener productos destacados
  getFeaturedProducts: async (limit: number = 3): Promise<Product[]> => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('featured', true)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error al obtener productos destacados:', error);
        return [];
      }

      return data.map(convertSupabaseProduct);
    } catch (error) {
      console.error('Error inesperado al obtener productos destacados:', error);
      return [];
    }
  },

  // Obtener productos por categoría
  getProductsByCategory: async (category: string): Promise<Product[]> => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('category', category)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error al obtener productos por categoría:', error);
        return [];
      }

      return data.map(convertSupabaseProduct);
    } catch (error) {
      console.error('Error inesperado al obtener productos por categoría:', error);
      return [];
    }
  }
};